import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { contactsApi } from '../api/contacts';
import './ContactsPage.css';

const STAGE_CONFIG = {
  all:         { label: 'All',         color: '#ffffff' },
  new:         { label: 'New',         color: '#6B7280' },
  contacted:   { label: 'Contacted',   color: '#00D9FF' },
  engaged:     { label: 'Engaged',     color: '#A855F7' },
  opportunity: { label: 'Opportunity', color: '#FFB800' },
  partner:     { label: 'Partner',     color: '#39FF14' },
  inactive:    { label: 'Inactive',    color: '#374151' },
};

const CONTACT_TYPES = ['all', 'artist', 'fan', 'eventOrganizer', 'label', 'agent', 'media', 'venue', 'publisher', 'artistManager', 'other'];

const containerVariants = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.04 } },
};

const rowVariants = {
  hidden: { opacity: 0, x: -12 },
  visible: { opacity: 1, x: 0, transition: { type: 'spring', stiffness: 260, damping: 22 } },
  exit: { opacity: 0, x: 12 },
};

export default function ContactsPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [contacts, setContacts] = useState([]);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);
  const [loading, setLoading] = useState(true);

  const stage = searchParams.get('stage') || 'all';
  const type = searchParams.get('type') || 'all';
  const search = searchParams.get('search') || '';
  const page = parseInt(searchParams.get('page') || '1');

  const updateParam = (key, value) => {
    const p = new URLSearchParams(searchParams);
    if (value && value !== 'all') p.set(key, value);
    else p.delete(key);
    if (key !== 'page') p.delete('page');
    setSearchParams(p);
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit: 24 };
      if (stage !== 'all') params.stage = stage;
      if (type !== 'all') params.type = type;
      if (search) params.search = search;
      const data = await contactsApi.getAll(params);
      setContacts(data.contacts || []);
      setTotal(data.total || 0);
      setPages(data.pages || 1);
    } catch (err) {
      console.error('[Contacts]', err.message);
    } finally {
      setLoading(false);
    }
  }, [stage, type, search, page]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="contacts-page">
      <div className="contacts-inner">
        <div className="contacts-header">
          <div>
            <h1 className="contacts-title">Contacts</h1>
            <p className="contacts-sub">{total} total contacts</p>
          </div>
        </div>

        {/* Filter Bar */}
        <div className="filter-bar">
          {/* Stage pills */}
          <div className="filter-stages">
            {Object.entries(STAGE_CONFIG).map(([s, cfg]) => (
              <button
                key={s}
                className={`stage-pill ${stage === s ? 'active' : ''}`}
                style={{ '--pill-color': cfg.color }}
                onClick={() => updateParam('stage', s)}
              >
                {cfg.label}
              </button>
            ))}
          </div>

          {/* Type + Search */}
          <div className="filter-controls">
            <select
              className="filter-select"
              value={type}
              onChange={e => updateParam('type', e.target.value)}
            >
              {CONTACT_TYPES.map(t => (
                <option key={t} value={t}>
                  {t === 'all' ? 'All Types' : t.charAt(0).toUpperCase() + t.slice(1).replace(/([A-Z])/g, ' $1')}
                </option>
              ))}
            </select>

            <div className="filter-search">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="11" cy="11" r="8" /><path d="m21 21-4.35-4.35" />
              </svg>
              <input
                type="text"
                placeholder="Search contacts..."
                value={search}
                onChange={e => updateParam('search', e.target.value)}
              />
            </div>
          </div>
        </div>

        {/* Contact List */}
        {loading ? (
          <div className="contacts-loading">
            <div className="contacts-spinner" />
          </div>
        ) : contacts.length === 0 ? (
          <div className="contacts-empty">
            <p>No contacts found</p>
            <span>Try adjusting your filters</span>
          </div>
        ) : (
          <AnimatePresence mode="wait">
            <motion.div
              key={`${stage}-${type}-${search}-${page}`}
              className="contacts-table-wrap"
              variants={containerVariants}
              initial="hidden"
              animate="visible"
              exit="hidden"
            >
              <table className="contacts-table">
                <thead>
                  <tr>
                    <th>Contact</th>
                    <th>Type</th>
                    <th>Stage</th>
                    <th>Score</th>
                    <th>Location</th>
                    <th>Last Activity</th>
                  </tr>
                </thead>
                <tbody>
                  {contacts.map(contact => {
                    const stageCfg = STAGE_CONFIG[contact.stage] || STAGE_CONFIG.new;
                    const name = contact.company || `${contact.firstName} ${contact.lastName || ''}`.trim();
                    const initial = name[0]?.toUpperCase() || '?';
                    const lastActivity = contact.lastInteractionAt || contact.updatedAt;
                    return (
                      <motion.tr
                        key={contact._id}
                        variants={rowVariants}
                        className="contact-row"
                        onClick={() => navigate(`/contacts/${contact._id}`)}
                        whileHover={{ backgroundColor: 'rgba(255,255,255,0.03)' }}
                      >
                        <td>
                          <div className="contact-cell">
                            <div
                              className="contact-avatar"
                              style={{
                                background: `${stageCfg.color}22`,
                                color: stageCfg.color,
                                border: `1px solid ${stageCfg.color}44`,
                              }}
                            >
                              {contact.avatarImage
                                ? <img src={contact.avatarImage} alt={name} />
                                : initial}
                            </div>
                            <div className="contact-name-wrap">
                              <span className="contact-name">{name}</span>
                              {contact.email && <span className="contact-email">{contact.email}</span>}
                            </div>
                          </div>
                        </td>
                        <td>
                          <span className="contact-type-text">
                            {contact.contactType?.charAt(0).toUpperCase() + contact.contactType?.slice(1).replace(/([A-Z])/g, ' $1')}
                          </span>
                        </td>
                        <td>
                          <span
                            className="stage-badge"
                            style={{
                              background: `${stageCfg.color}22`,
                              color: stageCfg.color,
                              border: `1px solid ${stageCfg.color}44`,
                            }}
                          >
                            {stageCfg.label}
                          </span>
                        </td>
                        <td>
                          <div className="score-cell">
                            <span className="score-num" style={{ color: contact.score >= 75 ? '#39FF14' : contact.score >= 40 ? '#FFB800' : '#6B7280' }}>
                              {contact.score}
                            </span>
                            <div className="score-bar">
                              <div
                                className="score-bar-fill"
                                style={{
                                  width: `${contact.score}%`,
                                  background: contact.score >= 75 ? '#39FF14' : contact.score >= 40 ? '#FFB800' : '#6B7280',
                                }}
                              />
                            </div>
                          </div>
                        </td>
                        <td>
                          <span className="contact-location">{contact.location || '—'}</span>
                        </td>
                        <td>
                          <span className="contact-date">
                            {lastActivity ? new Date(lastActivity).toLocaleDateString() : '—'}
                          </span>
                        </td>
                      </motion.tr>
                    );
                  })}
                </tbody>
              </table>
            </motion.div>
          </AnimatePresence>
        )}

        {/* Pagination */}
        {pages > 1 && (
          <div className="pagination">
            <button
              className="page-btn"
              disabled={page <= 1}
              onClick={() => updateParam('page', String(page - 1))}
            >
              Prev
            </button>
            <span className="page-info">Page {page} of {pages}</span>
            <button
              className="page-btn"
              disabled={page >= pages}
              onClick={() => updateParam('page', String(page + 1))}
            >
              Next
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
